import { useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { useThemeMode } from '_metronic/partials'
import { getCSS } from '_metronic/assets/ts/_utils'

interface Props {
  colors: string[]
  series: number[]
  labels: string[]
  height?: string
  donutSize?: string // New prop for donut hole size
}

const DonutChart: React.FC<Props> = ({
  colors,
  series,
  labels,
  height = '300px',
  donutSize = '50%', // Default donut hole size
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, mode, series, labels])

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const chartHeight = parseInt(getCSS(chartRef.current, 'height'))
    const chartWidth = parseInt(getCSS(chartRef.current, 'width'))

    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(chartHeight, chartWidth, colors, series, labels, donutSize)
    )
    if (chart) {
      chart.render()
    }

    return chart
  }

  return (
    <>
      {/* begin::Chart */}
      <div
        ref={chartRef}
        id='kt_charts_donut_chart'
        className='donut-chart'
        style={{ height, width: '100%' }}></div>
      {/* end::Chart */}
    </>
  )
}

function getChartOptions(
  height: number,
  width: number,
  colors: string[],
  series: number[],
  labels: string[],
  donutSize: string
): ApexOptions {
  // Check if there's no data (empty array or all values are 0)
  const hasNoData = series.length === 0 || series.every(value => value === 0)

  return {
    series: series,
    chart: {
      type: 'donut',
      height: height,
      width: '100%',
    },
    labels: labels,
    colors: colors,
    legend: {
      position: 'bottom',
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: '100%',
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
    plotOptions: {
      pie: {
        donut: {
          size: donutSize, // This creates the donut hole
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val.toFixed(1) + '%'
      },
    },
    noData: {
      text: 'ไม่มีข้อมูล',
      align: 'center',
      verticalAlign: 'middle',
      offsetX: 0,
      offsetY: 0,
      style: {
        color: '#6c757d',
        fontSize: '16px',
        fontFamily: 'Poppins',
      },
    },
  }
}

export default DonutChart
